import { useMemo } from 'react';
import isNil from 'lodash/isNil';
import { usePathname } from 'next/navigation';
import { isInBuilder } from '~/app/lib/helpers';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';
import sanitizeNumber from '~/app/lib/sanitize-number';
import { ConditionalRule } from './dynamic-grid-container-editor/types';
import { formatConditionalRulesAsString } from './utils';

export type ParentElementType = {
  index?: number;
  componentId?: string;
  info?: unknown[];
  style?: unknown[];
  tabletStyle?: unknown[];
  mobileStyle?: unknown[];
  layout?: unknown[];
  tabletLayout?: unknown[];
  mobileLayout?: unknown[];
  spacing?: unknown[];
  tabletSpacing?: unknown[];
  mobileSpacing?: unknown[];
  borders?: unknown[];
  tabletBorders?: unknown[];
  mobileBorders?: unknown[];
  dimensions?: unknown[];
  tabletDimensions?: unknown[];
  mobileDimensions?: unknown[];
  $unique_key?: string;
  componentName?: string;
  hide?: unknown[];
  hideOnDesktop?: boolean;
  hideOnMobile?: boolean;
  hideOnTablet?: boolean;
  conditionalShowRules?: ConditionalRule[];
  floating?: unknown[];
  tracking?: {
    subLocation: any;
    location: any;
    eventName: any;
    entityName: string;
    enabled: boolean;
    impressionPosition: string;
    impressionSection: string;
    marketplaceOfferId: string;
  };
};

const getContainerProp = (
  property: keyof ParentElementType,
  parentElement: ParentElementType
) => {
  // Handle tracking property differently since it's an object, not an array
  if (property === 'tracking') {
    return parentElement?.[property] || {};
  }

  if (property === 'conditionalShowRules') {
    return parentElement?.[property] || [];
  }

  // Ensure property value is an array before spreading
  const propertyValue = parentElement?.[property];
  if (Array.isArray(propertyValue)) {
    return Object.assign({}, ...propertyValue);
  }

  if (typeof propertyValue === 'object') {
    return propertyValue;
  }
  return {};
};

export const useDynamicGridContainer = ({
  parentElement,
}: {
  parentElement: ParentElementType;
}) => {
  const infoOptions = getContainerProp('info', parentElement);
  const styleOptions = getContainerProp('style', parentElement);
  const tabletStyleOptions = getContainerProp('tabletStyle', styleOptions);
  const mobileStyleOptions = getContainerProp('mobileStyle', styleOptions);
  const layoutOptions = getContainerProp('layout', parentElement);
  const tabletLayoutOptions = getContainerProp('tabletLayout', layoutOptions);
  const mobileLayoutOptions = getContainerProp('mobileLayout', layoutOptions);
  const spacingOptions = getContainerProp('spacing', parentElement);
  const tabletSpacingOptions = getContainerProp(
    'tabletSpacing',
    spacingOptions
  );
  const mobileSpacingOptions = getContainerProp(
    'mobileSpacing',
    spacingOptions
  );
  const bordersOptions = getContainerProp('borders', parentElement);
  const tabletBordersOptions = getContainerProp(
    'tabletBorders',
    bordersOptions
  );
  const mobileBordersOptions = getContainerProp(
    'mobileBorders',
    bordersOptions
  );
  const dimensionsOptions = getContainerProp('dimensions', parentElement);
  const tabletDimensionsOptions = getContainerProp(
    'tabletDimensions',
    dimensionsOptions
  );
  const mobileDimensionsOptions = getContainerProp(
    'mobileDimensions',
    dimensionsOptions
  );

  const hideOptions = getContainerProp('hide', parentElement) || {};

  const conditionalShowRulesOptions: ParentElementType['conditionalShowRules'] =
    getContainerProp('conditionalShowRules', parentElement);

  const floatingOptions = getContainerProp('floating', parentElement);
  const trackingOptions: ParentElementType['tracking'] = getContainerProp(
    'tracking',
    parentElement
  );

  const pathname = usePathname();
  const { disableGridOutline } = useDynamicGrid();
  const isPageInBuilder = isInBuilder(pathname);
  const { selected } = useBuilder();
  const isSelected = parentElement?.componentId === selected;
  const isGridOutlineDisabled = parentElement?.componentId
    ? disableGridOutline?.[
        parentElement.componentId as keyof typeof disableGridOutline
      ] || false
    : false;
  const shouldShowGridOutlineStyle =
    isPageInBuilder && isSelected && !isGridOutlineDisabled;

  const id = infoOptions?.id || undefined;

  // Display and Grid Template Columns
  const display = layoutOptions?.layout === 'grid' ? 'grid' : 'flex';
  const gridTemplateColumns =
    layoutOptions?.layout === 'grid'
      ? `repeat(${layoutOptions?.gridColumns}, 1fr)`
      : 'none';
  const gridTemplateColumnsTablet =
    layoutOptions?.layout === 'grid' && tabletLayoutOptions?.gridColumns
      ? `repeat(${tabletLayoutOptions?.gridColumns}, 1fr)`
      : gridTemplateColumns;
  const gridTemplateColumnsMobile =
    layoutOptions?.layout === 'grid' && mobileLayoutOptions?.gridColumns
      ? `repeat(${mobileLayoutOptions?.gridColumns}, 1fr)`
      : gridTemplateColumnsTablet;

  const position = layoutOptions?.isSticky ? 'sticky' : 'relative';
  const positionTablet = tabletLayoutOptions?.isSticky ? 'sticky' : 'relative';
  const positionMobile = mobileLayoutOptions?.isSticky ? 'sticky' : 'relative';
  const zIndex = layoutOptions?.isSticky ? 1 : undefined;
  const zIndexTablet = tabletLayoutOptions?.isSticky ? 1 : undefined;
  const zIndexMobile = mobileLayoutOptions?.isSticky ? 1 : undefined;
  const fullWidthStickyProps = {
    position: 'fixed',
    width: '100dvw',
    left: 0,
  };
  const isFullWidthSticky = layoutOptions?.isFullWidthSticky || undefined;
  const isFullWidthStickyTablet =
    tabletLayoutOptions?.isFullWidthSticky || undefined;
  const isFullWidthStickyMobile =
    mobileLayoutOptions?.isFullWidthSticky || undefined;

  const stickyTop = layoutOptions?.stickyTop
    ? `${layoutOptions?.stickyTop}px`
    : '96px';
  const stickyTopTablet = tabletLayoutOptions?.stickyTop
    ? `${tabletLayoutOptions?.stickyTop}px`
    : '0px';
  const stickyTopMobile = mobileLayoutOptions?.stickyTop
    ? `${mobileLayoutOptions?.stickyTop}px`
    : '0px';

  // Align and Justify Content
  const elementType = layoutOptions?.elementType || 'row';
  const elementTypeTablet = tabletLayoutOptions?.elementType || elementType;
  const elementTypeMobile =
    mobileLayoutOptions?.elementType || elementTypeTablet;

  const alignItems = layoutOptions?.alignItems || 'flex-start';
  const alignItemsTablet = tabletLayoutOptions?.alignItems || alignItems;
  const alignItemsMobile = mobileLayoutOptions?.alignItems || alignItemsTablet;

  const justifyContent = layoutOptions?.justifyContent || 'flex-start';
  const justifyContentTablet =
    tabletLayoutOptions?.justifyContent || justifyContent;
  const justifyContentMobile =
    mobileLayoutOptions?.justifyContent || justifyContentTablet;

  // Dimensions
  const widthUnit = dimensionsOptions?.widthUnit || 'px';
  const widthUnitTablet = tabletDimensionsOptions?.widthUnit || widthUnit;
  const widthUnitMobile = mobileDimensionsOptions?.widthUnit || widthUnitTablet;
  const minWidthUnit = dimensionsOptions?.minWidthUnit || 'px';
  const minWidthUnitTablet =
    tabletDimensionsOptions?.minWidthUnit || minWidthUnit;
  const minWidthUnitMobile =
    mobileDimensionsOptions?.minWidthUnit || minWidthUnitTablet;
  const heightUnit = dimensionsOptions?.heightUnit || 'px';
  const heightUnitTablet = tabletDimensionsOptions?.heightUnit || heightUnit;
  const heightUnitMobile =
    mobileDimensionsOptions?.heightUnit || heightUnitTablet;

  const width = !isNil(sanitizeNumber(dimensionsOptions?.width))
    ? `${sanitizeNumber(dimensionsOptions?.width)}${widthUnit}`
    : 'initial';
  const widthTablet = !isNil(sanitizeNumber(tabletDimensionsOptions?.width))
    ? `${sanitizeNumber(tabletDimensionsOptions?.width)}${widthUnitTablet}`
    : width;
  const widthMobile = !isNil(sanitizeNumber(mobileDimensionsOptions?.width))
    ? `${sanitizeNumber(mobileDimensionsOptions?.width)}${widthUnitMobile}`
    : widthTablet;

  const height = !isNil(sanitizeNumber(dimensionsOptions?.height))
    ? `${sanitizeNumber(dimensionsOptions?.height)}${heightUnit}`
    : 'initial';
  const heightTablet = !isNil(sanitizeNumber(tabletDimensionsOptions?.height))
    ? `${sanitizeNumber(tabletDimensionsOptions?.height)}${heightUnitTablet}`
    : height;
  const heightMobile = !isNil(sanitizeNumber(mobileDimensionsOptions?.height))
    ? `${sanitizeNumber(mobileDimensionsOptions?.height)}${heightUnitMobile}`
    : heightTablet;

  const minWidthOverflowScroll = !isNil(
    sanitizeNumber(dimensionsOptions?.minWidthOverflowScroll)
  )
    ? `${sanitizeNumber(
        dimensionsOptions?.minWidthOverflowScroll
      )}${minWidthUnit}`
    : 'initial';
  const minWidthOverflowScrollTablet = !isNil(
    sanitizeNumber(tabletDimensionsOptions?.minWidthOverflowScroll)
  )
    ? `${sanitizeNumber(
        tabletDimensionsOptions?.minWidthOverflowScroll
      )}${minWidthUnitTablet}`
    : minWidthOverflowScroll;
  const minWidthOverflowScrollMobile = !isNil(
    sanitizeNumber(mobileDimensionsOptions?.minWidthOverflowScroll)
  )
    ? `${sanitizeNumber(
        mobileDimensionsOptions?.minWidthOverflowScroll
      )}${minWidthUnitMobile}`
    : minWidthOverflowScrollTablet;

  const isOverflowScroll = dimensionsOptions?.isOverflowScroll
    ? 'auto'
    : undefined;
  const isOverflowScrollTablet = tabletDimensionsOptions?.isOverflowScroll
    ? 'auto'
    : undefined;
  const isOverflowScrollMobile = mobileDimensionsOptions?.isOverflowScroll
    ? 'auto'
    : undefined;

  // Spacing
  const paddingUnit = spacingOptions?.paddingUnit || 'px';
  const marginUnit = spacingOptions?.marginUnit || 'px';

  // Padding
  const paddingTop = !isNil(sanitizeNumber(spacingOptions?.paddingTop))
    ? `${sanitizeNumber(spacingOptions?.paddingTop) || 0}${paddingUnit}`
    : 'initial';
  const paddingTopTablet = !isNil(
    sanitizeNumber(tabletSpacingOptions?.paddingTop)
  )
    ? `${sanitizeNumber(tabletSpacingOptions?.paddingTop)}${paddingUnit}`
    : paddingTop;
  const paddingTopMobile = !isNil(
    sanitizeNumber(mobileSpacingOptions?.paddingTop)
  )
    ? `${sanitizeNumber(mobileSpacingOptions?.paddingTop)}${paddingUnit}`
    : paddingTopTablet;

  const paddingRight = !isNil(sanitizeNumber(spacingOptions?.paddingRight))
    ? `${sanitizeNumber(spacingOptions?.paddingRight) || 0}${paddingUnit}`
    : 'initial';
  const paddingRightTablet = !isNil(
    sanitizeNumber(tabletSpacingOptions?.paddingRight)
  )
    ? `${sanitizeNumber(tabletSpacingOptions?.paddingRight)}${paddingUnit}`
    : paddingRight;
  const paddingRightMobile = !isNil(
    sanitizeNumber(mobileSpacingOptions?.paddingRight)
  )
    ? `${sanitizeNumber(mobileSpacingOptions?.paddingRight)}${paddingUnit}`
    : paddingRightTablet;

  const paddingBottom = !isNil(sanitizeNumber(spacingOptions?.paddingBottom))
    ? `${sanitizeNumber(spacingOptions?.paddingBottom) || 0}${paddingUnit}`
    : 'initial';
  const paddingBottomTablet = !isNil(
    sanitizeNumber(tabletSpacingOptions?.paddingBottom)
  )
    ? `${sanitizeNumber(tabletSpacingOptions?.paddingBottom)}${paddingUnit}`
    : paddingBottom;
  const paddingBottomMobile = !isNil(
    sanitizeNumber(mobileSpacingOptions?.paddingBottom)
  )
    ? `${sanitizeNumber(mobileSpacingOptions?.paddingBottom)}${paddingUnit}`
    : paddingBottomTablet;

  const paddingLeft = !isNil(sanitizeNumber(spacingOptions?.paddingLeft))
    ? `${sanitizeNumber(spacingOptions?.paddingLeft) || 0}${paddingUnit}`
    : 'initial';
  const paddingLeftTablet = !isNil(
    sanitizeNumber(tabletSpacingOptions?.paddingLeft)
  )
    ? `${sanitizeNumber(tabletSpacingOptions?.paddingLeft)}${paddingUnit}`
    : paddingLeft;
  const paddingLeftMobile = !isNil(
    sanitizeNumber(mobileSpacingOptions?.paddingLeft)
  )
    ? `${sanitizeNumber(mobileSpacingOptions?.paddingLeft)}${paddingUnit}`
    : paddingLeftTablet;

  // Margins
  const marginTop = !isNil(sanitizeNumber(spacingOptions?.marginTop))
    ? `${sanitizeNumber(spacingOptions?.marginTop) || 0}${marginUnit}`
    : 'initial';
  const marginTopTablet = !isNil(
    sanitizeNumber(tabletSpacingOptions?.marginTop)
  )
    ? `${sanitizeNumber(tabletSpacingOptions?.marginTop)}${marginUnit}`
    : marginTop;
  const marginTopMobile = !isNil(
    sanitizeNumber(mobileSpacingOptions?.marginTop)
  )
    ? `${sanitizeNumber(mobileSpacingOptions?.marginTop)}${marginUnit}`
    : marginTopTablet;

  const marginRight = !isNil(sanitizeNumber(spacingOptions?.marginRight))
    ? `${sanitizeNumber(spacingOptions?.marginRight) || 0}${marginUnit}`
    : 'initial';
  const marginRightTablet = !isNil(
    sanitizeNumber(tabletSpacingOptions?.marginRight)
  )
    ? `${sanitizeNumber(tabletSpacingOptions?.marginRight)}${marginUnit}`
    : marginRight;
  const marginRightMobile = !isNil(
    sanitizeNumber(mobileSpacingOptions?.marginRight)
  )
    ? `${sanitizeNumber(mobileSpacingOptions?.marginRight)}${marginUnit}`
    : marginRightTablet;

  const marginBottom = !isNil(sanitizeNumber(spacingOptions?.marginBottom))
    ? `${sanitizeNumber(spacingOptions?.marginBottom) || 0}${marginUnit}`
    : 'initial';
  const marginBottomTablet = !isNil(
    sanitizeNumber(tabletSpacingOptions?.marginBottom)
  )
    ? `${sanitizeNumber(tabletSpacingOptions?.marginBottom)}${marginUnit}`
    : marginBottom;
  const marginBottomMobile = !isNil(
    sanitizeNumber(mobileSpacingOptions?.marginBottom)
  )
    ? `${mobileSpacingOptions?.marginBottom}${marginUnit}`
    : marginBottomTablet;

  const marginLeft = !isNil(sanitizeNumber(spacingOptions?.marginLeft))
    ? `${sanitizeNumber(spacingOptions?.marginLeft) || 0}${marginUnit}`
    : 'initial';
  const marginLeftTablet = !isNil(
    sanitizeNumber(tabletSpacingOptions?.marginLeft)
  )
    ? `${sanitizeNumber(tabletSpacingOptions?.marginLeft)}${marginUnit}`
    : marginLeft;
  const marginLeftMobile = !isNil(
    sanitizeNumber(mobileSpacingOptions?.marginLeft)
  )
    ? `${sanitizeNumber(mobileSpacingOptions?.marginLeft)}${marginUnit}`
    : marginLeftTablet;

  // Box Shadow
  const boxShadowColor = bordersOptions?.boxShadowColor || '#cdcdcd'; // Default from universal container
  const boxShadowColorTablet =
    tabletBordersOptions?.boxShadowColor || boxShadowColor;
  const boxShadowColorMobile =
    mobileBordersOptions?.boxShadowColor || boxShadowColorTablet;
  const boxShadow = bordersOptions?.boxShadow
    ? `0px 1px 10px -1px ${boxShadowColor}`
    : undefined;
  const boxShadowTablet = tabletBordersOptions?.boxShadow
    ? `0px 1px 10px -1px ${boxShadowColorTablet}`
    : undefined;
  const boxShadowMobile = mobileBordersOptions?.boxShadow
    ? `0px 1px 10px -1px ${boxShadowColorMobile}`
    : undefined;

  // Borders
  let borderColorDesktop = bordersOptions?.borderColor || 'transparent';
  borderColorDesktop =
    bordersOptions?.borderColor === 'HEXCOLOR'
      ? bordersOptions?.borderColorHex
      : borderColorDesktop;

  let borderColorTablet =
    tabletBordersOptions?.borderColor || borderColorDesktop;
  borderColorTablet =
    tabletBordersOptions?.borderColor === 'HEXCOLOR'
      ? tabletBordersOptions?.borderColorHex
      : borderColorTablet;

  let borderColorMobile =
    mobileBordersOptions?.borderColor || borderColorTablet;
  borderColorMobile =
    mobileBordersOptions?.borderColor === 'HEXCOLOR'
      ? mobileBordersOptions?.borderColorHex
      : borderColorMobile;

  const borderStyle = bordersOptions?.borderStyle || 'none';
  const borderStyleTablet = tabletBordersOptions?.borderStyle || borderStyle;
  const borderStyleMobile =
    mobileBordersOptions?.borderStyle || borderStyleTablet;

  const borderRadius = bordersOptions?.borderRadius || 0;
  const borderTopLeftRadius = !isNil(
    sanitizeNumber(bordersOptions?.borderTopLeftRadius)
  )
    ? bordersOptions?.borderTopLeftRadius
    : borderRadius;
  const borderTopLeftRadiusTablet = !isNil(
    sanitizeNumber(tabletBordersOptions?.borderTopLeftRadius)
  )
    ? tabletBordersOptions?.borderTopLeftRadius
    : borderTopLeftRadius;
  const borderTopLeftRadiusMobile = !isNil(
    sanitizeNumber(mobileBordersOptions?.borderTopLeftRadius)
  )
    ? mobileBordersOptions?.borderTopLeftRadius
    : borderTopLeftRadiusTablet;
  const borderTopRightRadius = !isNil(
    sanitizeNumber(bordersOptions?.borderTopRightRadius)
  )
    ? bordersOptions?.borderTopRightRadius
    : borderRadius;
  const borderTopRightRadiusTablet = !isNil(
    sanitizeNumber(tabletBordersOptions?.borderTopRightRadius)
  )
    ? tabletBordersOptions?.borderTopRightRadius
    : borderTopRightRadius;
  const borderTopRightRadiusMobile = !isNil(
    sanitizeNumber(mobileBordersOptions?.borderTopRightRadius)
  )
    ? mobileBordersOptions?.borderTopRightRadius
    : borderTopRightRadiusTablet;
  const borderBottomRightRadius = !isNil(
    sanitizeNumber(bordersOptions?.borderBottomRightRadius)
  )
    ? bordersOptions?.borderBottomRightRadius
    : borderRadius;
  const borderBottomRightRadiusTablet = !isNil(
    sanitizeNumber(tabletBordersOptions?.borderBottomRightRadius)
  )
    ? tabletBordersOptions?.borderBottomRightRadius
    : borderBottomRightRadius;
  const borderBottomRightRadiusMobile = !isNil(
    sanitizeNumber(mobileBordersOptions?.borderBottomRightRadius)
  )
    ? mobileBordersOptions?.borderBottomRightRadius
    : borderBottomRightRadiusTablet;
  const borderBottomLeftRadius = !isNil(
    sanitizeNumber(bordersOptions?.borderBottomLeftRadius)
  )
    ? bordersOptions?.borderBottomLeftRadius
    : borderRadius;
  const borderBottomLeftRadiusTablet = !isNil(
    sanitizeNumber(tabletBordersOptions?.borderBottomLeftRadius)
  )
    ? tabletBordersOptions?.borderBottomLeftRadius
    : borderBottomLeftRadius;
  const borderBottomLeftRadiusMobile = !isNil(
    sanitizeNumber(mobileBordersOptions?.borderBottomLeftRadius)
  )
    ? mobileBordersOptions?.borderBottomLeftRadius
    : borderBottomLeftRadiusTablet;

  const borderWidth = bordersOptions?.borderWidth || 0;
  const borderTopWidth = !isNil(sanitizeNumber(bordersOptions?.borderTopWidth))
    ? bordersOptions?.borderTopWidth
    : borderWidth;
  const borderTopWidthTablet = !isNil(
    sanitizeNumber(tabletBordersOptions?.borderTopWidth)
  )
    ? tabletBordersOptions?.borderTopWidth
    : borderTopWidth;
  const borderTopWidthMobile = !isNil(
    sanitizeNumber(mobileBordersOptions?.borderTopWidth)
  )
    ? mobileBordersOptions?.borderTopWidth
    : borderTopWidthTablet;
  const borderRightWidth = !isNil(
    sanitizeNumber(bordersOptions?.borderRightWidth)
  )
    ? bordersOptions?.borderRightWidth
    : borderWidth;
  const borderRightWidthTablet = !isNil(
    sanitizeNumber(tabletBordersOptions?.borderRightWidth)
  )
    ? tabletBordersOptions?.borderRightWidth
    : borderRightWidth;
  const borderRightWidthMobile = !isNil(
    sanitizeNumber(mobileBordersOptions?.borderRightWidth)
  )
    ? mobileBordersOptions?.borderRightWidth
    : borderRightWidthTablet;
  const borderBottomWidth = !isNil(
    sanitizeNumber(bordersOptions?.borderBottomWidth)
  )
    ? bordersOptions?.borderBottomWidth
    : borderWidth;
  const borderBottomWidthTablet = !isNil(
    sanitizeNumber(tabletBordersOptions?.borderBottomWidth)
  )
    ? tabletBordersOptions?.borderBottomWidth
    : borderBottomWidth;
  const borderBottomWidthMobile = !isNil(
    sanitizeNumber(mobileBordersOptions?.borderBottomWidth)
  )
    ? mobileBordersOptions?.borderBottomWidth
    : borderBottomWidthTablet;
  const borderLeftWidth = !isNil(
    sanitizeNumber(bordersOptions?.borderLeftWidth)
  )
    ? bordersOptions?.borderLeftWidth
    : borderWidth;
  const borderLeftWidthTablet = !isNil(
    sanitizeNumber(tabletBordersOptions?.borderLeftWidth)
  )
    ? tabletBordersOptions?.borderLeftWidth
    : borderLeftWidth;
  const borderLeftWidthMobile = !isNil(
    sanitizeNumber(mobileBordersOptions?.borderLeftWidth)
  )
    ? mobileBordersOptions?.borderLeftWidth
    : borderLeftWidthTablet;

  // Background
  const combineGradientAndImage = (
    gradient: string | undefined,
    image: string | undefined
  ): string | undefined => {
    if (gradient && image) return `${gradient}, ${image}`;
    return gradient || image;
  };

  const backgroundGradient = styleOptions?.backgroundGradient || undefined;
  const backgroundGradientTablet =
    tabletStyleOptions?.backgroundGradient || backgroundGradient;
  const backgroundGradientMobile =
    mobileStyleOptions?.backgroundGradient || backgroundGradientTablet;

  // Raw image URLs resolved per breakpoint (before gradient is layered on top)
  const rawImageDesktop = styleOptions?.backgroundImageDesktop
    ? `url(${styleOptions.backgroundImageDesktop})`
    : undefined;
  const rawImageTablet = tabletStyleOptions?.backgroundImageDesktop
    ? `url(${tabletStyleOptions.backgroundImageDesktop})`
    : rawImageDesktop;
  const rawImageMobile = mobileStyleOptions?.backgroundImageDesktop
    ? `url(${mobileStyleOptions.backgroundImageDesktop})`
    : rawImageTablet;

  const backgroundImageDesktop = combineGradientAndImage(
    backgroundGradient,
    rawImageDesktop
  );
  const backgroundImageTablet = combineGradientAndImage(
    backgroundGradientTablet,
    rawImageTablet
  );
  const backgroundImageMobile = combineGradientAndImage(
    backgroundGradientMobile,
    rawImageMobile
  );

  const backgroundSize = styleOptions?.backgroundSize || 'cover';
  const backgroundSizeTablet =
    tabletStyleOptions?.backgroundSize || backgroundSize;
  const backgroundSizeMobile =
    mobileStyleOptions?.backgroundSize || backgroundSizeTablet;

  const backgroundRepeat = styleOptions?.backgroundRepeat || 'no-repeat';
  const backgroundRepeatTablet =
    tabletStyleOptions?.backgroundRepeat || backgroundRepeat;
  const backgroundRepeatMobile =
    mobileStyleOptions?.backgroundRepeat || backgroundRepeatTablet;

  const backgroundPositionXDistance = !isNil(
    sanitizeNumber(styleOptions?.backgroundPositionXDistance)
  )
    ? `${sanitizeNumber(styleOptions?.backgroundPositionXDistance)}px`
    : '0%';
  const backgroundPositionXDistanceTablet = !isNil(
    sanitizeNumber(tabletStyleOptions?.backgroundPositionXDistance)
  )
    ? `${sanitizeNumber(tabletStyleOptions?.backgroundPositionXDistance)}px`
    : '0%';
  const backgroundPositionXDistanceMobile = !isNil(
    sanitizeNumber(mobileStyleOptions?.backgroundPositionXDistance)
  )
    ? `${sanitizeNumber(mobileStyleOptions?.backgroundPositionXDistance)}px`
    : '0%';

  const backgroundPositionX = styleOptions?.backgroundPositionX || 'left';
  const backgroundPositionXTablet =
    tabletStyleOptions?.backgroundPositionX || backgroundPositionX;
  const backgroundPositionXMobile =
    mobileStyleOptions?.backgroundPositionX || backgroundPositionXTablet;

  const backgroundPositionYDistance = !isNil(
    sanitizeNumber(styleOptions?.backgroundPositionYDistance)
  )
    ? `${sanitizeNumber(styleOptions?.backgroundPositionYDistance)}px`
    : '0%';
  const backgroundPositionYDistanceTablet = !isNil(
    sanitizeNumber(tabletStyleOptions?.backgroundPositionYDistance)
  )
    ? `${sanitizeNumber(tabletStyleOptions?.backgroundPositionYDistance)}px`
    : '0%';
  const backgroundPositionYDistanceMobile = !isNil(
    sanitizeNumber(mobileStyleOptions?.backgroundPositionYDistance)
  )
    ? `${sanitizeNumber(mobileStyleOptions?.backgroundPositionYDistance)}px`
    : '0%';

  const backgroundPositionY = styleOptions?.backgroundPositionY || 'top';
  const backgroundPositionYTablet =
    tabletStyleOptions?.backgroundPositionY || backgroundPositionY;
  const backgroundPositionYMobile =
    mobileStyleOptions?.backgroundPositionY || backgroundPositionYTablet;

  const backgroundPositionValue = useMemo(() => {
    if (backgroundPositionY === 'center' && backgroundPositionX === 'center') {
      return 'center';
    }
    if (backgroundPositionY === 'center') {
      return `center ${backgroundPositionX} ${backgroundPositionXDistance}`;
    }
    if (backgroundPositionX === 'center') {
      return `${backgroundPositionY} ${backgroundPositionYDistance} center`;
    }
    return `${backgroundPositionY} ${backgroundPositionYDistance} ${backgroundPositionX} ${backgroundPositionXDistance}`;
  }, [
    backgroundPositionY,
    backgroundPositionYDistance,
    backgroundPositionX,
    backgroundPositionXDistance,
  ]);
  const backgroundPositionTablet = useMemo(() => {
    return `${backgroundPositionYTablet} ${backgroundPositionYDistanceTablet} ${backgroundPositionXTablet} ${backgroundPositionXDistanceTablet}`;
  }, [
    backgroundPositionYTablet,
    backgroundPositionYDistanceTablet,
    backgroundPositionXTablet,
    backgroundPositionXDistanceTablet,
  ]);
  const backgroundPositionMobile = useMemo(() => {
    return `${backgroundPositionYMobile} ${backgroundPositionYDistanceMobile} ${backgroundPositionXMobile} ${backgroundPositionXDistanceMobile}`;
  }, [
    backgroundPositionYMobile,
    backgroundPositionYDistanceMobile,
    backgroundPositionXMobile,
    backgroundPositionXDistanceMobile,
  ]);

  // Background Color
  const backgroundColor =
    styleOptions?.backgroundColor === 'HEXCOLOR'
      ? styleOptions?.backgroundColorHex
      : styleOptions?.backgroundColor;

  const backgroundColorTablet =
    styleOptions?.tabletStyle?.backgroundColor === 'HEXCOLOR'
      ? styleOptions?.tabletStyle?.backgroundColorHex
      : styleOptions?.tabletStyle?.backgroundColor;
  const backgroundColorMobile =
    styleOptions?.mobileStyle?.backgroundColor === 'HEXCOLOR'
      ? styleOptions?.mobileStyle?.backgroundColorHex
      : styleOptions?.mobileStyle?.backgroundColor;

  const tabletContainerOptions = {
    gridTemplateColumns: gridTemplateColumnsTablet,
    position: positionTablet,
    top: positionTablet === 'sticky' ? stickyTopTablet : undefined,
    elementType: elementTypeTablet,
    alignItems: alignItemsTablet,
    justifyContent: justifyContentTablet,
    width: widthUnitTablet === 'auto' ? 'auto' : widthTablet,
    height: heightUnitTablet === 'auto' ? 'auto' : heightTablet,
    paddingTop: paddingTopTablet,
    paddingRight: paddingRightTablet,
    paddingBottom: paddingBottomTablet,
    paddingLeft: paddingLeftTablet,
    marginTop: marginTopTablet,
    marginRight: marginRightTablet,
    marginBottom: marginBottomTablet,
    marginLeft: marginLeftTablet,
    minWidthOverflowScroll:
      minWidthUnitTablet === 'auto' ? 'auto' : minWidthOverflowScrollTablet,
    borderColor: borderColorTablet,
    borderStyle: borderStyleTablet,
    borderRadius: `${borderTopLeftRadiusTablet}px ${borderTopRightRadiusTablet}px ${borderBottomRightRadiusTablet}px ${borderBottomLeftRadiusTablet}px`,
    borderWidth: `${borderTopWidthTablet}px ${borderRightWidthTablet}px ${borderBottomWidthTablet}px ${borderLeftWidthTablet}px`,
    boxShadow: boxShadowTablet,
    backgroundImage: backgroundImageTablet,
    backgroundPosition: backgroundPositionTablet,
    backgroundSize: backgroundSizeTablet,
    backgroundRepeat: backgroundRepeatTablet,
    zIndex: zIndexTablet,
    isOverflowScroll: isOverflowScrollTablet,
    isFullWidthSticky: isFullWidthStickyTablet,
    backgroundColor: backgroundColorTablet || backgroundColor,
  };
  const mobileContainerOptions = {
    gridTemplateColumns: gridTemplateColumnsMobile,
    position: positionMobile,
    top: positionMobile === 'sticky' ? stickyTopMobile : undefined,
    elementType: elementTypeMobile,
    alignItems: alignItemsMobile,
    justifyContent: justifyContentMobile,
    width: widthUnitMobile === 'auto' ? 'auto' : widthMobile,
    height: heightUnitMobile === 'auto' ? 'auto' : heightMobile,
    paddingTop: paddingTopMobile,
    paddingRight: paddingRightMobile,
    paddingBottom: paddingBottomMobile,
    paddingLeft: paddingLeftMobile,
    marginTop: marginTopMobile,
    marginRight: marginRightMobile,
    marginBottom: marginBottomMobile,
    marginLeft: marginLeftMobile,
    minWidthOverflowScroll:
      minWidthUnitMobile === 'auto' ? 'auto' : minWidthOverflowScrollMobile,
    borderColor: borderColorMobile,
    borderStyle: borderStyleMobile,
    borderRadius: `${borderTopLeftRadiusMobile}px ${borderTopRightRadiusMobile}px ${borderBottomRightRadiusMobile}px ${borderBottomLeftRadiusMobile}px`,
    borderWidth: `${borderTopWidthMobile}px ${borderRightWidthMobile}px ${borderBottomWidthMobile}px ${borderLeftWidthMobile}px`,
    boxShadow: boxShadowMobile,
    backgroundImage: backgroundImageMobile,
    backgroundPosition: backgroundPositionMobile,
    backgroundSize: backgroundSizeMobile,
    backgroundRepeat: backgroundRepeatMobile,
    zIndex: zIndexMobile,
    isFullWidthSticky: isFullWidthStickyMobile,
    isOverflowScroll: isOverflowScrollMobile,
    backgroundColor:
      backgroundColorMobile || backgroundColorTablet || backgroundColor,
  };

  const hideOnTablet = hideOptions?.hideOnTablet || false;
  const hideOnMobile = hideOptions?.hideOnMobile || false;
  const hideOnDesktop = hideOptions?.hideOnDesktop || false;

  const floatingEnabled = floatingOptions?.enabled || false;
  const floatingTop = floatingOptions?.floatTop ?? '';
  const floatingBottom = floatingOptions?.floatBottom ?? '';
  const floatingLeft = floatingOptions?.floatLeft ?? '';
  const floatingRight = floatingOptions?.floatRight ?? '';
  const floatingIndex = floatingOptions?.floatIndex || undefined;
  const floatingUnit = floatingOptions?.floatUnit || 'px';

  const floatingStyle = {
    position: 'absolute',
    ...(floatingTop && { top: `${floatingTop}${floatingUnit}` }),
    ...(floatingBottom && { bottom: `${floatingBottom}${floatingUnit}` }),
    ...(floatingLeft && { left: `${floatingLeft}${floatingUnit}` }),
    ...(floatingRight && { right: `${floatingRight}${floatingUnit}` }),
    zIndex: floatingIndex,
  };

  const formattedConditionalShowRules = formatConditionalRulesAsString(
    conditionalShowRulesOptions || []
  );

  return {
    id,
    display,
    gridTemplateColumns,
    position,
    top: position === 'sticky' ? stickyTop : undefined,
    alignItems,
    justifyContent,
    width: widthUnit === 'auto' ? 'auto' : width,
    height: heightUnit === 'auto' ? 'auto' : height,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    minWidthOverflowScroll:
      minWidthUnit === 'auto' ? 'auto' : minWidthOverflowScroll,
    // borderRadius,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderBottomLeftRadius,
    borderColor: borderColorDesktop,
    // borderWidth,
    borderTopWidth,
    borderRightWidth,
    borderBottomWidth,
    borderLeftWidth,
    borderStyle,
    boxShadow,
    shouldShowGridOutlineStyle,
    styletOptions: styleOptions,
    elementType,
    // Background
    backgroundImage: backgroundImageDesktop,
    backgroundSize,
    backgroundRepeat,
    backgroundPosition: backgroundPositionValue,
    backgroundColor,
    zIndex,
    fullWidthStickyProps,
    isFullWidthSticky,
    isOverflowScroll,
    floatingEnabled,
    floatingStyle,
    // hide
    hideOnTablet,
    hideOnMobile,
    hideOnDesktop,
    formattedConditionalShowRules,
    // tracking
    trackingOptions,
    tabletContainerOptions,
    mobileContainerOptions,
  };
};
